import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { debounce } from '@mui/material';
import * as Ably from 'ably';
import { useChannel } from 'ably/react';

import { CfsApi, UserApi } from '@/api';
import { IcMarkerUser, ICRapidSOS } from '@/assets/images';
import { Loader } from '@/components';
import { IMarker, UserMarkerInfoItem } from '@/components/GoogleMap';
import { CallerLocationInfo } from '@/components/GoogleMap/CallerLocationInfo';
import { MapBreadcrumbItem } from '@/components/GoogleMap/MapBreadcrumb';
import { useRapidSos } from '@/hooks/useRapidSos';
import { ICallerLocation, ICFS, IFile } from '@/models';
import { RapidSOSInfoModal } from '@/pages/CFS/Edit/components/RapidSOSInfoModal';
import { useCFSNavigationStore, useToastStore, useUserStore } from '@/store';

// Constants
const NEARBY_USERS_TIMEOUT_SECONDS = 300; // 5 minutes
const AI_DESCRIPTION_DEBOUNCE_MS = 500;

type UnitShiftDistances = Record<string, number>;

// Utility functions for CFS updates
const updateCfsReporter = (cfs: ICFS, data: any): ICFS => {
  const updatedCfs = { ...cfs };

  if (data.reportTime) {
    updatedCfs.reporter = {
      ...updatedCfs.reporter,
      reportTime: data.reportTime,
    };
  }

  if (data.reporter) {
    const nextReporter = {
      ...updatedCfs.reporter,
      ...data.reporter,
    };
    if (
      'contactPhoneNumber' in data.reporter &&
      data.reporter.contactPhoneNumber === null
    ) {
      nextReporter.contactPhoneNumber = undefined;
    }
    updatedCfs.reporter = nextReporter;
  }

  return updatedCfs;
};

const updateCfsAssignedUnitShifts = (cfs: ICFS, newUnitShift: any): ICFS => {
  const currentAssignedUnitShifts = cfs.assignedUnitShifts?.slice() || [];
  const indexOfExist = currentAssignedUnitShifts.findIndex(
    ({ _id }) => _id === newUnitShift._id,
  );

  if (indexOfExist > -1) {
    currentAssignedUnitShifts[indexOfExist] = {
      ...currentAssignedUnitShifts[indexOfExist],
      ...newUnitShift,
    };
  } else if (newUnitShift.unitShift) {
    currentAssignedUnitShifts.push(newUnitShift);
  }

  return {
    ...cfs,
    assignedUnitShifts: currentAssignedUnitShifts,
  };
};

const updateCfsFromAblyMessage = (prevCfs: ICFS, messageData: any): ICFS => {
  let newState = { ...prevCfs };
  const preserveWhenMissingKeys: Array<keyof ICFS> = [
    'names',
    'vehicles',
    'properties',
  ];

  // Handle reporter updates
  newState = updateCfsReporter(newState, messageData);

  // Handle assigned unit shifts updates
  if (messageData.assignedUnitShifts) {
    newState = updateCfsAssignedUnitShifts(
      newState,
      messageData.assignedUnitShifts,
    );
  }

  // Handle primary reporter updates
  if ('primaryReporter' in messageData) {
    if (messageData.primaryReporter === null) {
      newState.primaryReporter = undefined;
    } else if (
      typeof messageData.primaryReporter === 'object' &&
      '_id' in messageData.primaryReporter &&
      'type' in messageData.primaryReporter
    ) {
      newState.primaryReporter = messageData.primaryReporter;
    }
  }

  // Handle other properties
  const excludedKeys = [
    'reportTime',
    'reporter',
    'assignedUnitShifts',
    'primaryReporter',
  ];
  Object.keys(messageData).forEach((key) => {
    if (!excludedKeys.includes(key)) {
      (newState as any)[key] = messageData[key];
    }
  });

  preserveWhenMissingKeys.forEach((key) => {
    if (
      !(key in messageData) &&
      prevCfs[key] !== undefined &&
      prevCfs[key] !== null
    ) {
      (newState as any)[key] = prevCfs[key];
    }
  });

  return newState;
};

interface CFSContextValue {
  cfs: ICFS | null;
  unitShiftDistances: UnitShiftDistances;
  mapBounds?: google.maps.LatLngBoundsLiteral;
  cfsMapMarkers?: IMarker[];
  locationMapMarkers?: IMarker[];
  fetchCFS: (options?: { force?: boolean }) => Promise<ICFS | null>;
  updateCFS: (data: ICFS | null | ((prev: ICFS | null) => ICFS | null)) => void;
  setMapBounds: (newBounds?: google.maps.LatLngBoundsLiteral) => void;
  handleRefresh: () => void;
  ablyCfsChannel?: Ably.Types.RealtimeChannelPromise;
  isFetching?: boolean;
  locations?: ICallerLocation[];
  locationBreadCrumb?: MapBreadcrumbItem[];
  additionalData?: any;
}

interface CFSProviderProps {
  cfsId?: string;
  children: ReactNode;
}

export const CFSContext = createContext<CFSContextValue>({
  cfs: null,
  unitShiftDistances: {},
  fetchCFS: async () => null,
  updateCFS: () => {},
  setMapBounds: () => {},
  handleRefresh: () => {},
  isFetching: false,
});

export const CFSProvider: FC<CFSProviderProps> = (props) => {
  const { cfsId, children } = props;
  const { updateToast } = useToastStore();
  const { account } = useUserStore();

  // State
  const [cfs, setCfs] = useState<ICFS | null>(null);
  const [mapBounds, setMapBounds] = useState<google.maps.LatLngBoundsLiteral>();
  const [cfsMapMarkers, setCfsMapMarkers] = useState<IMarker[] | undefined>();
  const [locationMapMarkers, setLocationMapMarkers] = useState<
    IMarker[] | undefined
  >();
  const [showCfsLocationInfoModal, setShowLocationInfoModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<
    ICallerLocation | undefined
  >();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { locations, additionalData } = useRapidSos(cfs);

  // Memoized values
  const locationBreadCrumb: MapBreadcrumbItem[] | undefined = useMemo(() => {
    if (!locations?.length) return undefined;

    return locations.map((l) => ({
      lat: Number(l.latitude),
      lng: Number(l.longitude),
      info: (
        <CallerLocationInfo
          timestamp={Number(l.created_time)}
          location={l.rg_address ?? ''}
          onInfoClick={() => {
            setSelectedLocation(l);
            setShowLocationInfoModal(true);
          }}
        />
      ),
    }));
  }, [locations]);

  const unitShiftDistances = useMemo(() => {
    if (!cfs?.unitShiftDistances) {
      return {};
    }
    return cfs.unitShiftDistances.reduce((obj: UnitShiftDistances, current) => {
      obj[current.unitShift] = current.distance;
      return obj;
    }, {});
  }, [cfs?.unitShiftDistances]);

  // Callbacks
  const fetchCFS = useCallback(
    async (options?: { force?: boolean }): Promise<ICFS | null> => {
      if (!cfsId) return null;

      // Check cache first for instant navigation unless force refresh requested.
      if (!options?.force) {
        const cached = useCFSNavigationStore.getState().getCFSFromCache(cfsId);
        if (cached) {
          setCfs(cached);
          return cached;
        }
      }

      setIsLoading(true);
      try {
        const res = await CfsApi.getOne(String(cfsId));
        // Cache the result for future navigation
        useCFSNavigationStore.getState().setCFSCache(cfsId, res.data);
        setCfs(res.data);
        return res.data;
      } catch (err: any) {
        updateToast({ open: true, message: err.message });
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [cfsId, updateToast],
  );

  const updateCFS = useCallback(
    (data: ICFS | null | ((prev: ICFS | null) => ICFS | null)) => {
      setCfs((prev) => {
        const nextValue =
          typeof data === 'function' ? (data as any)(prev) : data;
        if (cfsId) {
          if (nextValue) {
            useCFSNavigationStore.getState().setCFSCache(cfsId, nextValue);
          } else {
            useCFSNavigationStore.getState().invalidateCFS(cfsId);
          }
        }
        return nextValue;
      });
    },
    [cfsId],
  );

  const handleCfsUpdate = useCallback((messageData: any) => {
    setCfs((prevCfs) => {
      if (!prevCfs) return prevCfs;
      return updateCfsFromAblyMessage(prevCfs, messageData);
    });
  }, []);

  const fetchNearbyHQUsers = useCallback(
    async (checkTime = true) => {
      try {
        if (!cfs?.wasReadyToDispatchAt && checkTime) return;

        // Only fetch nearby users within 5 minutes since cfs became readyToDispatch.
        const timeDifferenceFromNow =
          checkTime && !!cfs?.wasReadyToDispatchAt
            ? (new Date().getTime() -
                new Date(cfs?.wasReadyToDispatchAt).getTime()) /
              1000
            : 0;

        if (timeDifferenceFromNow < NEARBY_USERS_TIMEOUT_SECONDS) {
          const res = await UserApi.getNearByUsersFromHQ();
          const userMarkers = res.data.map((user) => ({
            key: String(user._id),
            title: user.fullName ?? user.email,
            position: {
              lng: user.location?.point?.coordinates[0] || 0,
              lat: user.location?.point?.coordinates[1] || 0,
            },
            info: (
              <UserMarkerInfoItem
                userName={user.fullName || user.email}
                badgeNumber={user.profile?.employmentInformation?.badgeNumber}
              />
            ),
            icon: <IcMarkerUser />,
          }));

          setCfsMapMarkers(userMarkers);
        } else {
          setCfsMapMarkers(undefined);
        }
      } catch (err: any) {
        updateToast({ open: true, message: err.message });
      }
    },
    [cfs?.wasReadyToDispatchAt, updateToast],
  );

  // Effects for location markers
  useEffect(() => {
    if (!locations?.length) {
      setLocationMapMarkers(undefined);
      return;
    }

    const latestLocation = locations[locations.length - 1];
    setLocationMapMarkers([
      {
        key: 'callerLocation',
        title: 'Caller location',
        zIndex: 1,
        info: (
          <CallerLocationInfo
            timestamp={Number(latestLocation.created_time)}
            location={latestLocation.rg_address ?? ''}
            onInfoClick={() => {
              setSelectedLocation(latestLocation);
              setShowLocationInfoModal(true);
            }}
          />
        ),
        position: {
          lng: Number(latestLocation.longitude),
          lat: Number(latestLocation.latitude),
        },
        icon: <ICRapidSOS />,
      },
    ]);
  }, [locations]);

  // Ably channel for CFS updates
  const { channel: ablyCfsChannel } = useChannel(
    `account:${account?._id}:cfs:${cfsId}`,
    useCallback(
      async (message: Ably.Types.Message) => {
        const messageData = JSON.parse(JSON.stringify(message.data));

        // Invalidate cache when CFS is updated so next navigation refetches
        if (cfsId) {
          useCFSNavigationStore.getState().invalidateCFS(cfsId);
        }

        if (message.name === 'cfs-files-update') {
          if (cfs?.number) {
            updateCFS({
              ...cfs,
              files: messageData as IFile[],
            });
          }
          return;
        }

        if (!cfs) return;

        if (messageData.isReadyToDispatch) {
          fetchNearbyHQUsers(false);
        }

        handleCfsUpdate(messageData);
      },
      [cfs, cfsId, updateCFS, fetchNearbyHQUsers, handleCfsUpdate],
    ),
  );

  // Ably channel for AI description updates
  useChannel(
    `account:${account?._id}:cfs:all`,
    debounce((message: Ably.Types.Message) => {
      if (message?.name === 'GENERATED_AI_DESCRIPTION') {
        if (message?.data?.updatedCFS?.additionalInfo?.length) {
          setCfs((prevCfs) => {
            if (!prevCfs) return prevCfs;

            return {
              ...prevCfs,
              additionalInfo: message?.data?.updatedCFS?.additionalInfo,
            };
          });
        }
      }
    }, AI_DESCRIPTION_DEBOUNCE_MS),
  );

  useEffect(() => {
    setCfs(null);
    if (cfsId) fetchCFS({ force: true });
  }, [cfsId, fetchCFS]);

  useEffect(() => {
    fetchNearbyHQUsers();
  }, [cfs?.wasReadyToDispatchAt, fetchNearbyHQUsers]);

  // Context value
  const contextValue = useMemo(
    () => ({
      cfs,
      unitShiftDistances,
      mapBounds,
      ablyCfsChannel,
      cfsMapMarkers,
      fetchCFS,
      updateCFS,
      setMapBounds,
      handleRefresh: fetchNearbyHQUsers,
      isFetching: isLoading,
      locationMapMarkers,
      locations,
      additionalData,
      locationBreadCrumb,
    }),
    [
      cfs,
      unitShiftDistances,
      mapBounds,
      ablyCfsChannel,
      cfsMapMarkers,
      fetchCFS,
      updateCFS,
      fetchNearbyHQUsers,
      isLoading,
      locationMapMarkers,
      locations,
      additionalData,
      locationBreadCrumb,
    ],
  );

  return (
    <CFSContext.Provider value={contextValue}>
      <RapidSOSInfoModal
        location={selectedLocation}
        onClose={() => setShowLocationInfoModal(false)}
        open={showCfsLocationInfoModal}
      />
      {children}
      <Loader open={isLoading} />
    </CFSContext.Provider>
  );
};
